/**
 * Pure vanilla DOM utilities for Flow node highlighting.
 * No React, no layout shift, auto-cleanup after animation.
 * Uses data-flow-id attributes as stable anchors.
 */

const GLOW_CLASS = 'flow-node-highlighted';
const STYLE_ID = 'flow-highlight-keyframes';

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .${GLOW_CLASS} {
      outline: 2px solid rgba(43, 127, 255, 0.85) !important;
      outline-offset: 3px !important;
      border-radius: 10px !important;
      animation: flow-node-pulse 1.6s cubic-bezier(0.4, 0, 0.2, 1) forwards !important;
      position: relative !important;
    }

    @keyframes flow-node-pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(43, 127, 255, 0.35),
                    0 0 24px rgba(43, 127, 255, 0.3);
        outline-color: rgba(43, 127, 255, 0.9);
      }
      40% {
        box-shadow: 0 0 0 10px rgba(43, 127, 255, 0.08),
                    0 0 40px rgba(43, 127, 255, 0.2);
        outline-color: rgba(43, 127, 255, 0.7);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(43, 127, 255, 0),
                    0 0 0 rgba(43, 127, 255, 0);
        outline-color: rgba(43, 127, 255, 0);
      }
    }
  `;

  document.head.appendChild(style);
}

/**
 * Highlight the DOM node anchored by data-flow-id.
 * Transient glow — auto-removes after animation, no layout shift.
 */
export function highlightNode(id: string): void {
  ensureStyles();

  // Sanitize selector input
  const escaped = id.replace(/[^\w\-]/g, '');
  const element = document.querySelector(`[data-flow-id="${escaped}"]`);
  if (!element) return;

  // Clear any existing highlights first
  clearAllHighlights();

  element.classList.add(GLOW_CLASS);

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });

  const onEnd = () => {
    element.classList.remove(GLOW_CLASS);
    element.removeEventListener('animationend', onEnd);
  };

  element.addEventListener('animationend', onEnd);

  // Fallback — remove after animation duration + buffer
  setTimeout(() => element.classList.remove(GLOW_CLASS), 2000);
}

export function clearAllHighlights(): void {
  document.querySelectorAll(`.${GLOW_CLASS}`).forEach((el) => {
    el.classList.remove(GLOW_CLASS);
  });
}
